import React, { useMemo } from "react";
import { useSearchParams } from "react-router";
import BookedIntake from "../../components/appointments/BookedIntake";
import { CareScreenerContext } from "../../context/CareScreenerContext";
import { UserContext } from "../../context/UserContext";
import useArrayIterator from "../../hooks/useArrayIterator";
import { AccountType, User } from "../../model/userModels";
import { Appointment } from "../../model/appointmentModels";
import { Candidate } from "../../services/CareScreenerService";

type Scenario = "single" | "multi-first" | "multi-last";

const ALEX: Candidate = {
  user: {
    accountType: AccountType.CHILD,
    id: 1001,
    familyId: 1,
    firstName: "Alex",
    lastName: "Doe",
    dateOfBirth: "2015-04-12"
  },
  insurancePlanName: "Aetna PPO",
  insuranceEligibilityStatus: "ELIGIBLE"
};

const SAM: Candidate = {
  user: {
    accountType: AccountType.CHILD,
    id: 1002,
    familyId: 1,
    firstName: "Sam",
    lastName: "Doe",
    dateOfBirth: "2017-08-03"
  },
  insurancePlanName: "Aetna PPO",
  insuranceEligibilityStatus: "ELIGIBLE"
};

const PARENT: User = {
  accountType: AccountType.PARENT,
  id: 999,
  familyId: 1,
  firstName: "Jane",
  lastName: "Doe",
  dateOfBirth: "1985-01-01"
};

const THERAPIST_ATTENDEE = {
  id: 500,
  firstName: "Riley",
  lastName: "Therapist",
  accountType: AccountType.THERAPIST
};

function buildScenario(scenario: Scenario): { candidates: Candidate[]; startingIndex: number } {
  if (scenario === "single") return { candidates: [ALEX], startingIndex: 0 };
  if (scenario === "multi-first") return { candidates: [ALEX, SAM], startingIndex: 0 };
  return { candidates: [ALEX, SAM], startingIndex: 1 };
}

export default function BookedIntakePreview() {
  const [params] = useSearchParams();
  const scenario = (params.get("scenario") as Scenario) || "single";

  const { candidates, startingIndex } = useMemo(() => buildScenario(scenario), [scenario]);
  const candidateIterator = useArrayIterator(candidates, startingIndex);

  const appointment: Appointment = useMemo(
    () => ({
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      appointmentType: { name: "Intake" },
      attendees: [THERAPIST_ATTENDEE]
    }),
    []
  );

  const careScreenerValue = useMemo(
    () => ({
      candidates,
      setCandidates: () => undefined,
      candidateIterator,
      insuranceEligibilityStatus: undefined,
      setInsuranceEligibilityStatus: () => undefined
    }),
    [candidates, candidateIterator]
  );

  const userValue = useMemo(
    () => ({
      authenticated: true,
      user: PARENT,
      loading: false,
      loginInProgress: false,
      setLoginInProgress: () => undefined,
      setAuthenticated: () => undefined
    }),
    []
  );

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: 20 }}>
      <ScenarioSwitcher current={scenario} />
      <UserContext.Provider value={userValue}>
        <CareScreenerContext.Provider value={careScreenerValue}>
          <BookedIntake
            appointment={appointment}
            continueButtonLabel="finish up"
            onContinueButtonPressed={() => alert("Continue pressed (preview only)")}
            onScheduleNextClientButtonPress={() => alert("Schedule next pressed (preview only)")}
          />
        </CareScreenerContext.Provider>
      </UserContext.Provider>
    </div>
  );
}

function ScenarioSwitcher({ current }: { current: Scenario }) {
  const link = (key: Scenario, label: string) => (
    <a
      href={`?scenario=${key}`}
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        marginRight: 8,
        textDecoration: "none",
        color: current === key ? "#FFFFFF" : "#2562D8",
        backgroundColor: current === key ? "#2562D8" : "#FFFFFF",
        border: "1px solid #2562D8",
        fontSize: 14,
        fontWeight: 600
      }}
    >
      {label}
    </a>
  );
  return (
    <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
      {link("single", "Single child")}
      {link("multi-first", "Multi-child — first")}
      {link("multi-last", "Multi-child — last")}
    </div>
  );
}
